import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const DAWSessionContext = createContext(null);

export function useDAWSession() {
  return useContext(DAWSessionContext);
}

const MAX_COLLABORATORS = 4;
const STATE_SYNC_INTERVAL = 300;
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB

export default function DAWSessionProvider({ socket, channelName, isHost, userId, children }) {
  const [sessionRole, setSessionRole] = useState(isHost ? "host" : "viewer");
  const [collaborators, setCollaborators] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sessionActive, setSessionActive] = useState(isHost);
  const [remoteState, setRemoteState] = useState(null);

  const syncTimerRef = useRef(null);
  const getDawStateRef = useRef(null);

  // Host: register a callback to get current DAW state for broadcasting
  const registerStateGetter = useCallback((getter) => {
    getDawStateRef.current = getter;
  }, []);

  // Host: start broadcasting DAW state
  useEffect(() => {
    if (!isHost || !socket || !sessionActive) return;
    syncTimerRef.current = setInterval(() => {
      if (getDawStateRef.current) {
        const state = getDawStateRef.current();
        socket.emit("daw:state", { channelName, state });
      }
    }, STATE_SYNC_INTERVAL);
    return () => {
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    };
  }, [isHost, socket, channelName, sessionActive]);

  // --- Socket event listeners ---
  useEffect(() => {
    if (!socket) return;

    // Session lifecycle
    const handleSessionStart = (data) => {
      if (data.channelName !== channelName) return;
      setSessionActive(true);
      if (!isHost) {
        setSessionRole("viewer");
        if (data.state) setRemoteState(data.state);
      }
    };

    const handleSessionEnd = (data) => {
      if (data.channelName !== channelName) return;
      setSessionActive(false);
      setRemoteState(null);
      setCollaborators([]);
      setPendingRequests([]);
      if (!isHost) setSessionRole("viewer");
    };

    // State sync (viewer/collaborator receives)
    const handleState = (data) => {
      if (data.channelName !== channelName || isHost) return;
      setRemoteState(data.state);
    };

    // Access request (host receives)
    const handleRequestAccess = (data) => {
      if (data.channelName !== channelName || !isHost) return;
      setPendingRequests((prev) => {
        if (prev.some((r) => r.userId === data.userId)) return prev;
        return [...prev, { userId: data.userId, username: data.username, avatar: data.avatar }];
      });
    };

    const handleCancelRequest = (data) => {
      if (data.channelName !== channelName || !isHost) return;
      setPendingRequests((prev) => prev.filter((r) => r.userId !== data.userId));
    };

    // Access granted (viewer receives)
    const handleAccessGranted = (data) => {
      if (data.channelName !== channelName) return;
      if (data.userId === userId) {
        setSessionRole("collaborator");
      }
      setCollaborators((prev) => {
        if (prev.some((c) => c.userId === data.userId)) return prev;
        return [...prev, { userId: data.userId, username: data.username, avatar: data.avatar }];
      });
    };

    // Access denied (viewer receives)
    const handleAccessDenied = (data) => {
      if (data.channelName !== channelName || data.userId !== userId) return;
      // Stay as viewer — request was denied
    };

    // Access revoked
    const handleAccessRevoked = (data) => {
      if (data.channelName !== channelName) return;
      if (data.userId === userId) {
        setSessionRole("viewer");
      }
      setCollaborators((prev) => prev.filter((c) => c.userId !== data.userId));
    };

    // Collaborator actions (host receives)
    const handleCollabUploadPad = (data) => {
      if (data.channelName !== channelName || !isHost) return;
      // This will be handled by DAWProvider via onCollabAction callback
      if (onCollabActionRef.current) {
        onCollabActionRef.current({ type: "upload-pad", ...data });
      }
    };

    const handleCollabRecordAudio = (data) => {
      if (data.channelName !== channelName || !isHost) return;
      if (onCollabActionRef.current) {
        onCollabActionRef.current({ type: "record-audio", ...data });
      }
    };

    const handleCollabToggleStep = (data) => {
      if (data.channelName !== channelName || !isHost) return;
      if (onCollabActionRef.current) {
        onCollabActionRef.current({ type: "toggle-step", ...data });
      }
    };

    // Pad sample broadcast (viewer/collaborator receives waveform data)
    const handlePadSample = (data) => {
      if (data.channelName !== channelName || isHost) return;
      if (onCollabActionRef.current) {
        onCollabActionRef.current({ type: "remote-pad-sample", ...data });
      }
    };

    // Audio track data broadcast
    const handleAudioTrackData = (data) => {
      if (data.channelName !== channelName || isHost) return;
      if (onCollabActionRef.current) {
        onCollabActionRef.current({ type: "remote-audio-track", ...data });
      }
    };

    socket.on("daw:session-start", handleSessionStart);
    socket.on("daw:session-end", handleSessionEnd);
    socket.on("daw:state", handleState);
    socket.on("daw:request-access", handleRequestAccess);
    socket.on("daw:cancel-request", handleCancelRequest);
    socket.on("daw:access-granted", handleAccessGranted);
    socket.on("daw:access-denied", handleAccessDenied);
    socket.on("daw:access-revoked", handleAccessRevoked);
    socket.on("daw:collab-upload-pad", handleCollabUploadPad);
    socket.on("daw:collab-record-audio", handleCollabRecordAudio);
    socket.on("daw:collab-toggle-step", handleCollabToggleStep);
    socket.on("daw:pad-sample", handlePadSample);
    socket.on("daw:audio-track-data", handleAudioTrackData);

    return () => {
      socket.off("daw:session-start", handleSessionStart);
      socket.off("daw:session-end", handleSessionEnd);
      socket.off("daw:state", handleState);
      socket.off("daw:request-access", handleRequestAccess);
      socket.off("daw:cancel-request", handleCancelRequest);
      socket.off("daw:access-granted", handleAccessGranted);
      socket.off("daw:access-denied", handleAccessDenied);
      socket.off("daw:access-revoked", handleAccessRevoked);
      socket.off("daw:collab-upload-pad", handleCollabUploadPad);
      socket.off("daw:collab-record-audio", handleCollabRecordAudio);
      socket.off("daw:collab-toggle-step", handleCollabToggleStep);
      socket.off("daw:pad-sample", handlePadSample);
      socket.off("daw:audio-track-data", handleAudioTrackData);
    };
  }, [socket, channelName, isHost, userId]);

  // Ref for collab action callback (set by DAWProvider)
  const onCollabActionRef = useRef(null);
  const registerCollabActionHandler = useCallback((handler) => {
    onCollabActionRef.current = handler;
  }, []);

  // --- Actions ---

  const startSession = useCallback(() => {
    if (!socket || !isHost) return;
    const state = getDawStateRef.current?.();
    socket.emit("daw:start-session", { channelName, state });
    setSessionActive(true);
  }, [socket, channelName, isHost]);

  const endSession = useCallback(() => {
    if (!socket || !isHost) return;
    socket.emit("daw:end-session", { channelName });
    setSessionActive(false);
    setCollaborators([]);
    setPendingRequests([]);
  }, [socket, channelName, isHost]);

  const requestAccess = useCallback((username, avatar) => {
    if (!socket || isHost) return;
    socket.emit("daw:request-access", { channelName, userId, username, avatar });
  }, [socket, channelName, userId, isHost]);

  const cancelRequest = useCallback(() => {
    if (!socket) return;
    socket.emit("daw:cancel-request", { channelName, userId });
  }, [socket, channelName, userId]);

  const grantAccess = useCallback((targetUserId, username, avatar) => {
    if (!socket || !isHost) return;
    if (collaborators.length >= MAX_COLLABORATORS) return;
    socket.emit("daw:grant-access", { channelName, userId: targetUserId, username, avatar });
    setPendingRequests((prev) => prev.filter((r) => r.userId !== targetUserId));
    setCollaborators((prev) => {
      if (prev.some((c) => c.userId === targetUserId)) return prev;
      return [...prev, { userId: targetUserId, username, avatar }];
    });
  }, [socket, channelName, isHost, collaborators.length]);

  const denyAccess = useCallback((targetUserId) => {
    if (!socket || !isHost) return;
    socket.emit("daw:deny-access", { channelName, userId: targetUserId });
    setPendingRequests((prev) => prev.filter((r) => r.userId !== targetUserId));
  }, [socket, channelName, isHost]);

  const revokeAccess = useCallback((targetUserId) => {
    if (!socket || !isHost) return;
    socket.emit("daw:revoke-access", { channelName, userId: targetUserId });
    setCollaborators((prev) => prev.filter((c) => c.userId !== targetUserId));
  }, [socket, channelName, isHost]);

  // Collaborator sends actions to host
  const sendCollabAction = useCallback((action) => {
    if (!socket || sessionRole !== "collaborator") return;
    if (action.type === "upload-pad") {
      if (action.audioData && action.audioData.byteLength > MAX_UPLOAD_SIZE) {
        console.warn("Sample too large (max 5MB)");
        return;
      }
      socket.emit("daw:collab-upload-pad", { channelName, userId, ...action });
    } else if (action.type === "record-audio") {
      if (action.audioData && action.audioData.byteLength > MAX_UPLOAD_SIZE) {
        console.warn("Recording too large (max 5MB)");
        return;
      }
      socket.emit("daw:collab-record-audio", { channelName, userId, ...action });
    } else if (action.type === "toggle-step") {
      socket.emit("daw:collab-toggle-step", { channelName, userId, ...action });
    }
  }, [socket, channelName, userId, sessionRole]);

  // Host broadcasts pad sample data to all viewers
  const broadcastPadSample = useCallback((padIndex, audioData, name) => {
    if (!socket || !isHost) return;
    socket.emit("daw:pad-sample", { channelName, padIndex, audioData, name });
  }, [socket, channelName, isHost]);

  // Host broadcasts audio track data to all viewers
  const broadcastAudioTrack = useCallback((trackId, audioData, name) => {
    if (!socket || !isHost) return;
    socket.emit("daw:audio-track-data", { channelName, trackId, audioData, name });
  }, [socket, channelName, isHost]);

  const value = {
    sessionRole,
    sessionActive,
    collaborators,
    pendingRequests,
    remoteState,
    maxCollaborators: MAX_COLLABORATORS,
    // Actions
    startSession,
    endSession,
    requestAccess,
    cancelRequest,
    grantAccess,
    denyAccess,
    revokeAccess,
    sendCollabAction,
    broadcastPadSample,
    broadcastAudioTrack,
    // Registration callbacks
    registerStateGetter,
    registerCollabActionHandler,
  };

  return (
    <DAWSessionContext.Provider value={value}>
      {children}
    </DAWSessionContext.Provider>
  );
}
